import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react'
import { Icon } from '@iconify/react'
import {
  RASPADINHA_IMAGE_ACCEPT,
  validateRaspadinhaImageFile,
} from '@/lib/raspadinha-campaigns/image-upload'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './RaspadinhaImageUploadField.module.css'

type RaspadinhaImageUploadFieldProps = {
  label: string
  hint: string
  observations?: string
  imageUrl: string
  onImageChange: (url: string, file?: File | null) => void
  /** `compact` reduz ainda mais a prévia (ex.: imagem do prêmio contemplado). */
  previewSize?: 'standard' | 'compact'
  className?: string
  disabled?: boolean
}

export function RaspadinhaImageUploadField({
  label,
  hint,
  observations,
  imageUrl,
  onImageChange,
  previewSize = 'standard',
  className,
  disabled = false,
}: RaspadinhaImageUploadFieldProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isReading, setIsReading] = useState(false)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)

  const previewUrl = localPreviewUrl ?? imageUrl

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
    }
  }, [localPreviewUrl])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''

    if (!file) {
      return
    }

    const validationError = validateRaspadinhaImageFile(file, label)
    if (validationError) {
      setErrorMessage(validationError)
      return
    }

    setErrorMessage(null)
    setIsReading(true)

    try {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
      const objectUrl = URL.createObjectURL(file)
      setLocalPreviewUrl(objectUrl)
      onImageChange(objectUrl, file)
    } catch {
      setErrorMessage('Não foi possível carregar a imagem. Tente outro arquivo.')
    } finally {
      setIsReading(false)
    }
  }

  const handleRemove = () => {
    setErrorMessage(null)
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl)
      setLocalPreviewUrl(null)
    }
    onImageChange('', null)
  }

  const isDisabled = disabled || isReading

  return (
    <div className={`${editStyles.editMediaBlock} ${className ?? ''}`.trim()}>
      <label className={editStyles.editLabel} htmlFor={inputId}>
        {label}
        <span className={editStyles.editLabelHint}>{hint}</span>
      </label>
      {observations ? <p className={styles.observations}>{observations}</p> : null}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={RASPADINHA_IMAGE_ACCEPT}
        className={editStyles.fileInput}
        onChange={handleFileChange}
        disabled={isDisabled}
      />
      <button
        type="button"
        className={`${editStyles.editUploadZone} ${previewUrl ? editStyles.editUploadZoneHasFile : ''} ${previewUrl ? styles.uploadZoneWithPreview : ''}`}
        onClick={() => inputRef.current?.click()}
        disabled={isDisabled}
      >
        {previewUrl ? (
          <div
            className={`${styles.previewWrap} ${previewSize === 'compact' ? styles.previewWrapCompact : ''}`}
          >
            <img src={previewUrl} alt={`Prévia: ${label}`} />
          </div>
        ) : (
          <>
            <Icon
              icon="mdi:image-plus-outline"
              style={{ fontSize: 28, color: 'var(--color-text-muted)' }}
              aria-hidden
            />
            <span className={editStyles.editUploadHint}>
              PNG, JPG, WEBP ou GIF, até 5 MB
            </span>
            <span className={editStyles.editUploadButton}>
              {isReading ? 'Carregando…' : 'Escolher imagem'}
            </span>
          </>
        )}
      </button>
      {previewUrl && !disabled ? (
        <button type="button" className={styles.removeButton} onClick={handleRemove}>
          Remover imagem
        </button>
      ) : null}
      {errorMessage ? (
        <p className={styles.errorMessage} role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}
